// apps/web/src/pages/dashboard/SettingsPage.tsx

import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

import { TProfileSettingsSchema, ProfileSettingsSchema } from '@/lib/validators/profile';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormDescription, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Loader2, Trash2, Bell, BellOff, BellRing, Smartphone, TestTube, RefreshCw } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PushDiagnostics } from '@/components/PushDiagnostics';
import { getAvatarUrl } from '@/lib/utils';

const AvatarUploader = ({
  currentAvatar,
  onUploadSuccess,
}: {
  currentAvatar?: string | null;
  onUploadSuccess: (filePath: string) => void;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles[0]) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) return;
    setIsLoading(true);
    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const { data } = await axios.post('/upload/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Call parent handler to update profile in DB
      onUploadSuccess(data.filePath);
    } catch (error) {
      toast.error('Avatar upload failed. Please try again.');
    } finally {
      setIsLoading(false);
      setFile(null);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
      <img
        src={file ? URL.createObjectURL(file) : getAvatarUrl(currentAvatar) || `https://avatar.vercel.sh/user.png`}
        alt="Avatar"
        className="w-20 h-20 rounded-full object-cover"
      />
      <div className="flex-grow w-full sm:w-auto">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer text-sm hover:border-primary ${
            isDragActive && 'border-primary'
          }`}
        >
          <input {...getInputProps()} />
          {file ? (
            <p className="font-semibold text-primary">{file.name}</p>
          ) : (
            <p>Drop image here or click to select</p>
          )}
        </div>
      </div>
      {file && (
        <Button type="button" onClick={handleUpload} disabled={isLoading} className="w-full sm:w-auto">
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Upload
        </Button>
      )}
    </div>
  );
};

interface SettingsFormProps {
  user?: { username?: string | null };
  profile?: { fullName?: string | null; isAvailableToHire?: boolean; profilePicturePath?: string | null };
  refetch: () => void;
}

const GeneralSettingsForm = ({ user, profile, refetch }: SettingsFormProps) => {
  const form = useForm<TProfileSettingsSchema>({
    resolver: zodResolver(ProfileSettingsSchema),
    defaultValues: { fullName: profile?.fullName || '', username: user?.username || '' },
  });
  const { mutate, isLoading } = trpc.user.updateSettings.useMutation({
    onSuccess: () => {
      toast.success('Settings saved!');
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleAvatarSuccess = (filePath: string) => {
    mutate({ profilePicturePath: filePath });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => mutate(data))}>
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>Update your avatar and public information.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <AvatarUploader currentAvatar={profile?.profilePicturePath} onUploadSuccess={handleAvatarSuccess} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <Label>Username</Label>
                  <FormControl>
                    <Input placeholder="@username" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <Label>Full Name</Label>
                  <FormControl>
                    <Input placeholder="Your full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
        <CardFooter className="border-t pt-6 flex justify-end">
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save General Settings
          </Button>
        </CardFooter>
      </form>
    </Form>
  );
};

const TransporterSettingsForm = ({ profile, refetch }: SettingsFormProps) => {
  const form = useForm({
    resolver: zodResolver(ProfileSettingsSchema),
    defaultValues: { isAvailableToHire: profile?.isAvailableToHire || false },
  });
  const { mutate, isLoading } = trpc.user.updateSettings.useMutation({
    onSuccess: () => {
      toast.success('Availability updated!');
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => mutate(data))}>
        <CardHeader>
          <CardTitle>Transporter Settings</CardTitle>
          <CardDescription>Manage your public availability and token wallet.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="isAvailableToHire"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <Label>Available for hire</Label>
                  <FormDescription>Let clients know you are available for jobs.</FormDescription>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label>Token Wallet</Label>
              <FormDescription>Pre-purchase tokens to accept jobs instantly.</FormDescription>
            </div>
            <Button type="button">Manage Wallet</Button>
          </div>
        </CardContent>
        <CardFooter className="border-t pt-6 flex justify-end">
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Availability
          </Button>
        </CardFooter>
      </form>
    </Form>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PUSH NOTIFICATIONS SETTINGS (Enhanced with better state handling)
// ═══════════════════════════════════════════════════════════════════════════

const PushNotificationSettings = () => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    enable,
    disable,
    showTestNotification,
    checkSubscription,
  } = usePushNotifications();

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      await enable();
    } else {
      await disable();
    }
  };

  const handleRefreshStatus = async () => {
    await checkSubscription();
    toast.success('Status refreshed');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            <CardTitle>Push Notifications</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefreshStatus}
            disabled={isLoading}
            title="Refresh notification status"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <CardDescription>
          Receive real-time updates about your jobs, bookings, and account activity
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Browser Support Warning */}
        {!isSupported && (
          <Alert variant="destructive">
            <BellOff className="w-4 h-4" />
            <AlertDescription>
              Push notifications are not supported in your browser. Please use Chrome, Firefox, or Safari.
            </AlertDescription>
          </Alert>
        )}

        {/* Permission Denied Warning */}
        {isSupported && permission === 'denied' && (
          <Alert variant="destructive">
            <BellOff className="w-4 h-4" />
            <AlertDescription>
              Notifications are blocked. Please enable them in your browser settings to receive updates.
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Checking notification status...</span>
          </div>
        ) : (
          <>
            {/* Main Toggle */}
            {isSupported && permission !== 'denied' && (
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="push-notifications" className="text-base font-medium">
                    Enable Push Notifications
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified about new jobs, bookings, and updates
                  </p>
                </div>
                <Switch
                  id="push-notifications"
                  checked={isSubscribed}
                  onCheckedChange={handleToggle}
                  disabled={isLoading}
                />
              </div>
            )}

            <Separator />

            {/* Status Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Status</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Browser Support</span>
                  <span className={isSupported ? 'text-green-600' : 'text-red-600'}>
                    {isSupported ? '✓ Supported' : '✗ Not Supported'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Permission</span>
                  <span className={permission === 'granted' ? 'text-green-600' : 'text-yellow-600'}>
                    {permission === 'granted'
                      ? '✓ Granted'
                      : permission === 'denied'
                      ? '✗ Denied'
                      : '⚠ Not Asked'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Subscription</span>
                  <span className={isSubscribed ? 'text-green-600' : 'text-muted-foreground'}>
                    {isSubscribed ? '✓ Active' : '○ Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Notification Types */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">You'll receive notifications for:</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <BellRing className="w-4 h-4 text-primary" />
                  <span>New job opportunities</span>
                </div>
                <div className="flex items-center gap-2">
                  <BellRing className="w-4 h-4 text-primary" />
                  <span>Booking confirmations</span>
                </div>
                <div className="flex items-center gap-2">
                  <BellRing className="w-4 h-4 text-primary" />
                  <span>Payment transactions</span>
                </div>
                <div className="flex items-center gap-2">
                  <BellRing className="w-4 h-4 text-primary" />
                  <span>Verification updates</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Test Notification */}
            {isSubscribed && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Test Notifications</h4>
                <p className="text-sm text-muted-foreground">
                  Send a test notification to verify everything is working
                </p>
                <Button variant="outline" size="sm" onClick={showTestNotification} className="w-full sm:w-auto">
                  <TestTube className="w-4 h-4 mr-2" />
                  Send Test Notification
                </Button>
              </div>
            )}

            {/* Info Note */}
            <Alert>
              <Smartphone className="w-4 h-4" />
              <AlertDescription>
                <strong>Tip:</strong> Notifications persist even when you refresh or close the app. They're tied to your
                browser, not the page session.
              </AlertDescription>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// MAIN SETTINGS PAGE
// ═══════════════════════════════════════════════════════════════════════════

const SettingsPage = () => {
  const { user: authUser } = useAuthStore();
  const { data: userProfile, isLoading, refetch } = trpc.user.getProfile.useQuery();

  if (isLoading)
    return (
      <div className="container py-12">
        <Skeleton className="h-[500px] w-full max-w-4xl mx-auto" />
      </div>
    );
  if (!userProfile || !userProfile.profile) return <p>Could not load user settings.</p>;

  return (
    <div className="container mx-auto py-6 px-2 sm:px-4 max-w-4xl space-y-6 sm:space-y-8 animate-fade-in">
      <header className="px-2">
        <h1 className="text-2xl sm:text-3xl font-bold">Account Settings</h1>
        <p className="text-muted-foreground text-sm sm:text-base">Manage your account and profile settings.</p>
      </header>

      {/* General Settings */}
      <Card>
        <GeneralSettingsForm user={userProfile} profile={userProfile.profile} refetch={refetch} />
      </Card>

      {/* Transporter Settings */}
      {authUser?.role === 'TRANSPORTER' && (
        <Card>
          <TransporterSettingsForm profile={userProfile.profile} refetch={refetch} />
        </Card>
      )}

      {/* Push Notifications Settings */}
      <PushNotificationSettings />
      {/* <PushDiagnostics /> */} 

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>This action is permanent and cannot be undone.</CardDescription>
        </CardHeader>
        <CardFooter className="bg-destructive/10 pt-6 flex justify-end">
          <Button type="button" variant="destructive">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Account
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SettingsPage;